import PubSub from 'underpub-js';
import BBox from '@turf/bbox';
import ImgixClient from "@imgix/js-core";
import tippy from 'tippy.js';
import 'tippy.js/animations/scale-subtle.css';
import gsap from 'gsap';

const client = new ImgixClient({
	domain: 'authenticffamplify.imgix.net',
});

var MapStandard = function (options) {
	this.options = {
		$el: false,
		baseClass: 'mol-MapStandard',
		settings: {
			style: 'mapbox://styles/authenticff/cm8x36qy700me01skaqtlh9pj',
			center: [-96, 40],
			zoom: 3,
		},
		colors: {
			featuredMarkerBackground: '#000000',
			markerBackground: '#0965b3',
			clusterBackground: '#0965b3',
			clusterText: '#FFFFFF',
		}
	};

	$.extend(true, this.options, options);
};

MapStandard.prototype.init = function () {

	const self = this;

	// colors
	let computedElementStyle = getComputedStyle(self.options.$el.get(0));
	self.options.colors.featuredMarkerBackground = computedElementStyle.getPropertyValue('--featured-marker-background-color');
	self.options.colors.markerBackground = computedElementStyle.getPropertyValue('--marker-background-color');
	self.options.colors.clusterBackground = computedElementStyle.getPropertyValue('--cluster-background-color');
	self.options.colors.clusterText = computedElementStyle.getPropertyValue('--cluster-number-color');

	self.mapPopupWrapperClass = `${self.options.baseClass}__popupWrapper`;
	self.mapPopupClass = `${self.options.baseClass}__popup`;
	self.mapPopupCloseClass = `${self.options.baseClass}__popup__close`;
	self.mapPopupButtonBaseClass = `atom-ButtonSecondary`;
	self.mapPopupButtonClasses = `
		${self.mapPopupButtonBaseClass}
		${self.mapPopupButtonBaseClass}--large
		${self.mapPopupButtonBaseClass}--underline
		${self.mapPopupButtonBaseClass}--${self.options.$el.data('theme')}
		${self.mapPopupButtonBaseClass}--filled
		${self.mapPopupButtonBaseClass}--hasText
	`;

	self.$map = self.options.$el.find(`.${self.options.baseClass}__map`);
	self.id = self.options.$el.attr('id');

	self.activePopupId = null;

	this.filterZoomMode = 'allPoints';
	this.allFeaturesData = [];
	this.propertiesGeoJsonFeatures = [];
	this.filteredPropertiesGeoJsonFeatures = [];

	this.filters = {
		regions: {
			value: '',
		},
		states: {
			value: '',
		},
		propertyTypes: {
			value: '',
		},
	}

	self.initMap();

	PubSub.subscribe("propertyListings:filterChanged", function(data) {
		self.filters[data.name].value = data.value;
		self.filterGeoJson();
	});
	PubSub.subscribe("propertyListings:filterReset", function() {
		for (const key in self.filters) {
			self.filters[key].value = '';
		}
		self.filterGeoJson();
	});
	PubSub.subscribe("propertyListings:resetFilterValue", function(data) {
		self.filters[data.name].value = '';
	});

	PubSub.subscribe("Sitewide:resized", function(){
		self.centerMapAroundFeatures();
	});

};

MapStandard.prototype.initMap = function () {
	const self = this;

	mapboxgl.accessToken =
		'pk.eyJ1IjoiYXV0aGVudGljZmYiLCJhIjoiUmtjWTY2byJ9.QeHZuWdaMRJlobq_RgXngw';

	self.map = new mapboxgl.Map({
		container: self.$map.get(0),
		style: self.options.settings.style,
		zoom: self.options.settings.zoom,
		center: self.options.settings.center,
	});

	self.map.doubleClickZoom.disable();
	this.map.addControl(new mapboxgl.NavigationControl());

	self.centerMapAroundFeatures();

	self.map.on('style.load', function () {
		self.initMapFeatures();
	});

	self.options.$el.on('click', '.'+self.mapPopupCloseClass, function() {
		let $popup = $(this).closest('.'+self.mapPopupClass);
		let popupId = $popup.attr('id');
		self.hideMapPopup(popupId);
	});

};

/*
 *
 */
MapStandard.prototype.initMapFeatures = function() {

	let self = this;

	// putting together our graph QL query for our map data
	let query = `
	{
		entries(section: ["properties"]) {
			id
			url
			title
			... on properties_property_Entry {
				properties_address
				commonListing_listingSummary
				commonListing_listingImageSmall {
					id
					title
					path
				}
				commonListing_listingImageWide {
					id
					title
					path
				}
				properties_location {
					lat
					lng
				}
				properties_state {
					id
					title
					slug
					... on states_state_Entry {
						state_region {
							id
							title
							slug
						}
					}
				}
				properties_type {
					id
					title
					slug
				}
			}
			sectionHandle
		}
	}
	`;

	// run the ajax query that gets our map data
	$.ajax({

		type: 'POST',
		url: '/api',
		data: { query: query },

		success: function(data) {
			self.allFeaturesData = data.data.entries;
			self.addAllFeaturesToGeoJson();
			self.filterGeoJson();
		},

		error: function(data) {
			console.log('ajax error');
			console.log(data);
		},

	});

};

MapStandard.prototype.addAllFeaturesToGeoJson = function() {

	let self = this;

	for (let i = 0; i < self.allFeaturesData.length; i++) {
		if (self.allFeaturesData[i].sectionHandle === 'properties') {
			self.addPropertiesToGeoJson(self.allFeaturesData[i]);
		}
	}

	// set our filtered geoJson to all features as a starting point
	self.filteredPropertiesGeoJsonFeatures = [...self.propertiesGeoJsonFeatures];

	self.addAllGeoJsonFeaturesToMap();

};

MapStandard.prototype.addPropertiesToGeoJson = function(propertyData) {

	let self = this;
	let state = propertyData.properties_state?.[0] ?? null;
	let region = null;
	if (state) {
		region = state.state_region?.[0] ?? null;
	}

	let propertyGeoJsonFeature = {
		"type": "Feature",
		"geometry": {
			"type": "Point",
			"coordinates": [ propertyData.properties_location.lng, propertyData.properties_location.lat ]
		},
		"properties": {
			"id": propertyData.id,
			"title": propertyData.title,
			"description": propertyData.commonListing_listingSummary,
			"address": propertyData.properties_address,
			"url": propertyData.url,
			"popupId": 'popup_' + propertyData.id,
			"region": region ? region.slug : '',
			"state": state ? state.slug : '',
			"type": propertyData.properties_type?.[0]?.slug ?? '',
			"smallImage": propertyData.commonListing_listingImageSmall?.map(image => {
				return {
					title: image?.title ?? '',
					path: image?.path ?? '',
				}
			})[0],
			"wideImage": propertyData.commonListing_listingImageWide?.map(image => {
				return {
					title: image?.title ?? '',
					path: image?.path ?? '',
				}
			})[0],
		}
	};

	self.propertiesGeoJsonFeatures.push(propertyGeoJsonFeature);
};

MapStandard.prototype.addAllGeoJsonFeaturesToMap = function() {

	let self = this;

	// add properties to map
	self.map.addSource('properties', {
		'type': 'geojson',
		'data': {
			"type": "FeatureCollection",
			"features": self.propertiesGeoJsonFeatures
		},
		cluster: true,
		clusterMaxZoom: 11,
		clusterRadius: 160
	});

	self.map.addLayer({
		id: 'clusters',
		type: 'circle',
		source: 'properties',
		filter: ['has', 'point_count'],
		paint: {
			'circle-color': self.options.colors.clusterBackground,
			'circle-opacity': ['step', ['get', 'point_count'], 1, 20, 1, 50, 1],
			'circle-radius': ['step', ['get', 'point_count'], 20, 20, 30, 50, 40],
			'circle-stroke-width': 4,
			'circle-stroke-color': "#616161"
		},
	});

	self.map.addLayer({
		id: 'cluster-count',
		type: 'symbol',
		source: 'properties',
		filter: ['has', 'point_count'],
		layout: {
			'text-field': '{point_count_abbreviated}',
			'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
			'text-size': ['step', ['get', 'point_count'], 12, 20, 15, 50, 18],
		},
		paint: {
			'text-color': self.options.colors.clusterText,
		},
	});

	self.map.addLayer({
		id: 'unclustered-point',
		type: 'circle',
		source: 'properties',
		filter: [
			"all",
			['!', ['has', 'point_count']],
			['!=', ['get', 'isFeatured'], true]
		],
		paint: {
			'circle-color': self.options.colors.markerBackground,
			'circle-opacity': 1,
			'circle-radius': 16,
		},
	});

	self.map.addLayer({
		id: 'unclustered-point-image',
		type: 'symbol',
		source: 'properties',
		layout: {
			'icon-image': ['get', 'title', ['get', "icon"]],
			'icon-size': .5,
		}
	});

	self.registerMapMouseEvents();

};

MapStandard.prototype.registerMapMouseEvents = function () {
	let self = this;

	// map popups
	self.propertiesGeoJsonFeatures.forEach(feature => {

		const featureCoordinates = feature.geometry.coordinates;
		const featureProperties = feature.properties;
		const popupId = featureProperties.popupId;
		const featureHeading = featureProperties.title;
		const featureCopy = featureProperties.address;
		// const featureButton = "View Properties";
		const featureButtonTarget = "_blank";
		const featureButtonUrl = featureProperties.url;
		const featureButtonText = "View Property";
		let buttonMarkup = '';

		if (featureButtonUrl && featureButtonText) {
			buttonMarkup = `
				<div class="${self.mapPopupClass}__button">
					<a class="${self.mapPopupButtonClasses}" href="${featureButtonUrl}" target="${featureButtonTarget}">
						<span class="${self.mapPopupButtonBaseClass}__inner">
							<span class="${self.mapPopupButtonBaseClass}__text">${featureButtonText}</span>
						</span>
					</a>
				</div>
			`;
		}

		const popupMarkup = `
			<div class="${self.mapPopupWrapperClass}">
				<div class="${self.mapPopupClass}" id="${popupId}">
					<div class="${self.mapPopupClass}__close"></div>
					<div class="${self.mapPopupClass}__content">
						<h5 class="${self.mapPopupClass}__heading">
							${featureHeading}
						</h5>
						<div class="${self.mapPopupClass}__copy">
							${featureCopy}
						</div>
						${buttonMarkup}
					</div>
				</div>
			</div>
		`;

		const $popup = $(popupMarkup);

		// add popup to map
		let mapboxMarker = new mapboxgl.Marker({
			element: $popup[0],
			offset: [0, 0],
			anchor: 'bottom',
		})
			.setLngLat(featureCoordinates)
			.addTo(self.map);

	});

	// cluster click
	self.map.on('click', 'clusters', function (e) {
		const features = self.map.queryRenderedFeatures(e.point, {
			layers: ['clusters'],
		});
		const clusterId = features[0].properties.cluster_id;
		self.map
			.getSource('properties')
			.getClusterExpansionZoom(clusterId, function (err, zoom) {
				if (err) return;

				self.map.easeTo({
					center: features[0].geometry.coordinates,
					zoom: zoom,
				});
			});
	});

	// cluster hover
	self.map.on('mouseenter', 'clusters', function () {
		self.map.getCanvas().style.cursor = 'pointer';
	});

	self.map.on('mouseleave', 'clusters', function () {
		self.map.getCanvas().style.cursor = '';
	});

	// marker hover
	self.map.on('mouseenter', 'unclustered-point', function (e) {
		self.map.getCanvas().style.cursor = 'pointer';
	});

	self.map.on('mouseleave', 'unclustered-point', function (e) {
		self.map.getCanvas().style.cursor = '';
	});

	// marker click
	self.map.on('click', 'unclustered-point', function (e) {
		let popupId = e.features[0].properties.popupId;
		let featureLatLng = e.features[0].geometry.coordinates;
		self.showMapPopup(popupId, featureLatLng);
	});
}

MapStandard.prototype.filterGeoJson = function() {

	let self = this;


	this.filteredPropertiesGeoJsonFeatures = [...self.propertiesGeoJsonFeatures];

	if (self.filters.regions.value !== '') {
		self.filteredPropertiesGeoJsonFeatures = self.filteredPropertiesGeoJsonFeatures.filter(function(geoJsonObject) {
			return geoJsonObject.properties.region === self.filters.regions.value;
		});
	}
	if (self.filters.states.value !== '') {
		self.filteredPropertiesGeoJsonFeatures = self.filteredPropertiesGeoJsonFeatures.filter(function(geoJsonObject) {
			return geoJsonObject.properties.state === self.filters.states.value;
		});
	}
	if (self.filters.propertyTypes.value !== '') {
		self.filteredPropertiesGeoJsonFeatures = self.filteredPropertiesGeoJsonFeatures.filter(function(geoJsonObject) {
			return geoJsonObject.properties.type === self.filters.propertyTypes.value;
		});
	}

	PubSub.publish(`${self.options.baseClass}:newDataAvailable`, {
		data: self.filteredPropertiesGeoJsonFeatures
	});

	this.updateMapFeatures();

};

MapStandard.prototype.updateMapFeatures = function() {

	let self = this;
	self.hideMapPopup(self.activePopupId);

	let propertiesGeoJson = {
		"type": "FeatureCollection",
		"features": self.filteredPropertiesGeoJsonFeatures
	};

	this.map.getSource('properties').setData(propertiesGeoJson);

	if (this.filterZoomMode === 'allPoints') {
		this.centerMapAroundFeatures();
	}
	else {
		if (this.map.getZoom() > 14) {
			this.map.zoomTo(14);
		}
	}

};

MapStandard.prototype.centerMapAroundFeatures = function () {

	const self = this;

	let boundingBox = BBox({
		type: 'FeatureCollection',
		features: self.filteredPropertiesGeoJsonFeatures,
	});

	let boundingBoxPaddingValue = $(window).width() >= 768 ? 100 : 60;
	let boundingBoxPadding = {
		top: boundingBoxPaddingValue,
		bottom: boundingBoxPaddingValue,
		left: boundingBoxPaddingValue,
		right: boundingBoxPaddingValue,
	};

	if (self.filteredPropertiesGeoJsonFeatures.length > 0) {
		self.map.fitBounds(boundingBox, {
			padding: boundingBoxPadding,
			maxZoom: 14,
		});
	}

};

MapStandard.prototype.showMapPopup = function(popupId, latLng) {

	const self = this;
	let $popup = $('#'+popupId);
	let $popupWrapper = $popup.closest('.'+self.mapPopupWrapperClass);

	if (latLng) {
		self.map.panTo(latLng);
	}

	if (self.activePopupId != popupId) {

		self.hideMapPopup(self.activePopupId);
		self.activePopupId = popupId;

		gsap
			.timeline()
			.set($popupWrapper, {
				zIndex: 1
			})
			.to($popup,
				{
					ease: "back.out(1.4)",
					duration: 0.3,
					scale: 1,
					opacity: 1,
				}
			);

	}

};

MapStandard.prototype.hideMapPopup = function(popupId) {

	if (!popupId) return;

	const self = this;
	let $popup = $('#'+popupId);
	let $popupWrapper = $popup.closest('.'+self.mapPopupWrapperClass);
	self.activePopupId = null;

	gsap
		.timeline()
		.to($popup,
			{
				duration: 0.3,
				scale: 0,
				opacity: 0,
			}
		)
		.set($popupWrapper, {
			zIndex: -1
		});

};



export default MapStandard;
